/**
 * Currency configuration interface
 */
interface CurrencyConfig {
  code: string;
  symbol: string;
  decimals: number;
}

/**
 * Currency configurations
 * Add new currencies here - this is the only place that needs updating
 */
const CURRENCY_CONFIGS: CurrencyConfig[] = [
  { code: 'USD', symbol: '$', decimals: 2 },
  { code: 'GBP', symbol: '£', decimals: 2 },
  { code: 'SGD', symbol: 'S$', decimals: 2 },
  // Add more currencies as needed:
  // { code: 'EUR', symbol: '€', decimals: 2 },
  // { code: 'JPY', symbol: '¥', decimals: 0 },
  // { code: 'AUD', symbol: 'A$', decimals: 2 },
];

/**
 * Map of currency codes to their configurations for quick lookup
 */
const CURRENCY_MAP = new Map<string, CurrencyConfig>(CURRENCY_CONFIGS.map((config) => [config.code, config]));

/**
 * Get currency configuration
 * @param currency - Currency code
 * @returns Currency configuration or USD as fallback
 */
const getCurrencyConfig = (currency: string): CurrencyConfig => {
  let config = CURRENCY_MAP.get(currency?.toUpperCase());
  if (!config) {
    // USD is guaranteed to exist in CURRENCY_CONFIGS, fallback to it
    config = CURRENCY_MAP.get('USD') as CurrencyConfig;
  }
  return config;
};

/**
 * Format price with currency symbol, proper decimal places, and thousands separators
 * Uses Intl.NumberFormat for proper locale-aware formatting, with custom symbol override when needed
 * @param amount - The price amount as a number
 * @param currency - Currency code (USD, GBP, SGD, etc.)
 * @returns Formatted price string with currency symbol and proper formatting
 * @example
 * formatPrice(23.9, 'USD') // '$23.90'
 * formatPrice(1234.5, 'GBP') // '£1,234.50'
 * formatPrice(15999.99, 'SGD') // 'S$15,999.99'
 * formatPrice(1234, 'JPY') // '¥1,234' (no decimals)
 */
export const formatPrice = (amount: number, currency: string): string => {
  const config = getCurrencyConfig(currency);

  try {
    // Use Intl.NumberFormat for proper locale-aware currency formatting
    let formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config.code,
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals,
    }).format(amount);

    // Replace Intl's default symbol with custom symbol if they differ
    // This handles cases like SGD where Intl uses "SGD " but we want "S$"
    const intlSymbolMatch = formatted.match(/^(-)?([A-Z]{3})\s/);
    if (intlSymbolMatch) {
      const [fullMatch, minus = '', intlCode] = intlSymbolMatch;
      if (intlCode !== config.symbol) {
        // Replace "SGD " with "S$" while preserving minus sign
        formatted = formatted.replace(fullMatch, `${minus}${config.symbol}`);
      }
    }

    return formatted;
  } catch (error) {
    // Fallback for unsupported currencies or environments without Intl
    console.error('Error formatting price with Intl.NumberFormat:', error);

    // Manual fallback formatting - preserve exact decimal values without rounding
    const amountStr = amount.toString();
    const parts = amountStr.split('.');

    // Add thousands separator to integer part
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Handle decimal part - pad or truncate to match config.decimals
    if (config.decimals > 0) {
      let decimalPart = parts[1] || '';
      // Truncate if too long (no rounding)
      if (decimalPart.length > config.decimals) {
        decimalPart = decimalPart.slice(0, config.decimals);
      }
      // Pad with zeros if needed
      decimalPart = decimalPart.padEnd(config.decimals, '0');
      return `${config.symbol}${integerPart}.${decimalPart}`;
    }

    return `${config.symbol}${integerPart}`;
  }
};
